import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, CheckCircle2, CircleDashed, Loader2 } from "lucide-react";

const VERDICT = {
  PASS: ["Indexed", "bg-sage text-white"],
  NEUTRAL: ["Not indexed", "bg-[#EFEADF] text-[#7A6B4F]"],
  FAIL: ["Problem", "bg-[#F3DEDA] text-[#9C4221]"],
  not_checked: ["Not checked yet", "bg-[#EDEAE3] text-[#6E6A63]"],
};

const SEVERITY = {
  high: ["text-[#B4762F]", AlertTriangle],
  medium: ["text-[#9A968E]", AlertTriangle],
  low: ["text-[#9A968E]", CircleDashed],
};

const KIND = {
  verified: ["Google reported", "bg-sage text-white"],
  technical: ["Measured on our pages", "bg-[#E7EFE8] text-sage-dark"],
  advice: ["Our recommendation", "bg-[#EDEAE3] text-[#6E6A63]"],
};

const Stat = ({ label, value, testId }) => (
  <div className="bg-white border border-[#E6E3DC] p-4" data-testid={testId}>
    <div className="text-[10px] tracking-[0.16em] uppercase text-[#9A968E]">{label}</div>
    <div className="display text-[24px] text-charcoal mt-1">{value}</div>
  </div>
);

/**
 * Indexing and SEO health. Three sources stay visibly separate: what Google reported, what we
 * measured on our own pages, and what we recommend. Nothing here is an estimate.
 */
export const SeoHealth = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    api
      .get("/admin/seo-health")
      .then((r) => live && setData(r.data))
      .catch(() => live && setData(false));
    return () => {
      live = false;
    };
  }, []);

  if (data === null)
    return (
      <p className="text-[13px] text-[#6E6A63]" data-testid="seo-health-loading">
        Checking indexing and on-page health…
      </p>
    );
  if (data === false)
    return (
      <p className="text-[13px] text-[#B4762F]" data-testid="seo-health-error">
        Could not load the audit.
      </p>
    );

  const sm = data.sitemap;

  return (
    <div data-testid="seo-health">
      {data.refreshing && (
        <p
          className="text-[12.5px] text-[#6E6A63] mb-4 flex items-center gap-2"
          data-testid="seo-health-refreshing"
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin text-sage" />
          Asking Google about each URL in the background — takes a few minutes. Reopen this tab to
          see the rest.
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="In sitemap" value={data.counts.sitemap_urls} testId="seo-stat-sitemap" />
        <Stat label="Indexed by Google" value={data.counts.indexed} testId="seo-stat-indexed" />
        <Stat label="Not indexed" value={data.counts.not_indexed} testId="seo-stat-not-indexed" />
        <Stat
          label="With impressions (90d)"
          value={data.counts.with_impressions_90d}
          testId="seo-stat-impressions"
        />
      </div>

      {sm && (
        <p className="text-[12.5px] text-[#6E6A63] mt-4 leading-relaxed">
          Sitemap <span className="text-[#454545]">{sm.path}</span> — submitted{" "}
          {sm.lastSubmitted?.slice(0, 10)}, last read by Google{" "}
          {sm.lastDownloaded?.slice(0, 10)}, {sm.errors} errors, {sm.warnings} warnings. Google's
          sitemap "indexed" counter is a legacy figure and is not used here; the per-URL verdicts
          below come from the URL Inspection API instead.
        </p>
      )}

      <h2 className="text-[11px] tracking-[0.18em] uppercase text-[#9A968E] mt-9 mb-3">
        Issues, worst first
      </h2>
      <div className="space-y-3">
        {data.issues.length === 0 && (
          <p className="text-[13px] text-[#6E6A63]">Nothing outstanding.</p>
        )}
        {data.issues.map((i) => {
          const [colour, Icon] = SEVERITY[i.severity] || SEVERITY.low;
          const [kindLabel, kindClass] = KIND[i.kind] || KIND.advice;
          return (
            <div key={i.issue} className="bg-white border border-[#E6E3DC] p-4">
              <div className="flex items-start gap-3">
                <Icon className={`w-4 h-4 mt-1 shrink-0 ${colour}`} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[14px] text-charcoal">{i.issue}</span>
                    <span
                      className={`px-2 py-0.5 text-[10px] tracking-[0.1em] uppercase ${kindClass}`}
                    >
                      {kindLabel}
                    </span>
                  </div>
                  <p className="text-[13px] text-[#6E6A63] mt-1.5 leading-relaxed break-words">
                    {i.detail}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <h2 className="text-[11px] tracking-[0.18em] uppercase text-[#9A968E] mt-9 mb-3">
        Every URL in the sitemap
      </h2>
      <div className="bg-white border border-[#E6E3DC] overflow-x-auto">
        <table className="w-full min-w-[560px] text-[13px]">
          <thead>
            <tr className="border-b border-[#EDEAE3]">
              <th className="text-left text-[10px] tracking-[0.16em] uppercase text-[#9A968E] font-normal p-4">
                Page
              </th>
              <th className="text-left text-[10px] tracking-[0.16em] uppercase text-[#9A968E] font-normal">
                Google's verdict
              </th>
              <th className="text-right text-[10px] tracking-[0.16em] uppercase text-[#9A968E] font-normal pr-4">
                Impr. 90d
              </th>
            </tr>
          </thead>
          <tbody>
            {data.index_rows.map((r) => {
              const [label, cls] = VERDICT[r.google_verdict] || VERDICT.not_checked;
              return (
                <tr key={r.path} className="border-b border-[#F3F1EC] last:border-0">
                  <td className="p-4 text-[#454545] break-all">{r.path}</td>
                  <td>
                    <span
                      className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.1em] uppercase ${cls}`}
                    >
                      {label}
                    </span>
                    {r.coverage_state && (
                      <div className="text-[12px] text-[#9A968E] mt-1">{r.coverage_state}</div>
                    )}
                  </td>
                  <td className="text-right pr-4 text-[#6E6A63]">{r.impressions_90d}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h2 className="text-[11px] tracking-[0.18em] uppercase text-[#9A968E] mt-9 mb-3">
        Priority categories
      </h2>
      <div className="grid gap-3 md:grid-cols-2">
        {data.categories.map((c) => (
          <div key={c.category} className="bg-white border border-[#E6E3DC] p-4">
            <div className="flex items-start justify-between gap-3">
              <h3 className="display text-[16px] text-charcoal">{c.category}</h3>
              <span className="inline-flex items-center gap-1 text-[12px] text-[#6E6A63] shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5 text-sage" />
                {c.pages_indexed_by_google}/{c.pages.length} indexed
              </span>
            </div>
            <p className="text-[12.5px] text-[#6E6A63] mt-2">
              {c.pages.length} page{c.pages.length === 1 ? "" : "s"} ·{" "}
              {c.pages_with_impressions} with impressions · {c.internal_links_in} internal links in
            </p>
            {c.page_issues.length > 0 && (
              <p className="text-[12.5px] text-[#B4762F] mt-2">{c.page_issues.join(" · ")}</p>
            )}
          </div>
        ))}
      </div>

      <ul className="mt-8 space-y-2">
        {data.notes.map((n) => (
          <li key={n} className="text-[12px] text-[#9A968E] leading-relaxed">
            {n}
          </li>
        ))}
      </ul>
    </div>
  );
};
